/* eslint-disable no-process-env */

import crypto from 'crypto';

import React from 'react';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';

import {ErrorPageTypes, SamlErrorMessages, ServerErrorIds} from 'utils/constants';
import WarningIcon from 'components/widgets/icons/fa_warning_icon';

import {LockScreenForNoLicensesToSignIn} from 'features/license/components/lock-screen/no_more_license';

import ErrorTitle from './error_title';
import ErrorMessage from './error_message';

type Location = {
    search: string;
};

type Props = {
    location: Location;
    asymmetricSigningPublicKey?: string;
    siteName?: string;
    isGuest?: boolean;
    setAccessDenied: () => void;
}

const isDevOrTest = Boolean(process.env.IS_DEV) || (process.env.NODE_ENV === 'test');

export default class ErrorPage extends React.PureComponent<Props> {
    public componentDidMount() {
        document.body.setAttribute('class', 'sticky error');
    }

    public componentWillUnmount() {
        document.body.removeAttribute('class');
    }

    public render() {
        const {isGuest} = this.props;
        const params: URLSearchParams = new URLSearchParams(this.props.location.search);
        const signature = params.get('s');

        let trustParams = isDevOrTest;
        if (signature) {
            params.delete('s');

            const key = this.props.asymmetricSigningPublicKey;
            const keyPEM = '-----BEGIN PUBLIC KEY-----\n' + key + '\n-----END PUBLIC KEY-----';

            const verify = crypto.createVerify('sha256');
            verify.update('/error?' + params.toString());
            trustParams = verify.verify(keyPEM, signature, 'base64');
        }

        const type = params.get('type');
        const errorId = params.get('error_id');
        const title = (trustParams && params.get('title')) || '';
        const message = (trustParams && params.get('message')) || '';
        const service = (trustParams && params.get('service')) || '';
        const returnTo = (trustParams && params.get('returnTo')) || '';

        if (errorId === ServerErrorIds.LICENSE_LIMIT) {
            return <LockScreenForNoLicensesToSignIn/>;
        }

        if (this.isSAMLError(message)) {
            this.props.setAccessDenied();
            return null;
        }

        let backButton;
        if (type === ErrorPageTypes.PERMALINK_NOT_FOUND && returnTo) {
            backButton = (
                <Link to={returnTo}>
                    <FormattedMessage
                        id='error.generic.link'
                        defaultMessage='Back to Mattermost'
                    />
                </Link>
            );
        } else if (type === ErrorPageTypes.TEAM_NOT_FOUND) {
            backButton = (
                <Link to='/'>
                    <FormattedMessage
                        id='error.generic.link'
                        defaultMessage='Back to {siteName}'
                        values={{
                            siteName: this.props.siteName,
                        }}
                    />
                </Link>
            );
        } else if (type === ErrorPageTypes.CHANNEL_NOT_FOUND && isGuest) {
            backButton = (
                <Link to='/'>
                    <FormattedMessage
                        id='error.channelNotFound.guest_link'
                        defaultMessage='Back'
                    />
                </Link>
            );
        } else if (type === ErrorPageTypes.CHANNEL_NOT_FOUND) {
            backButton = (
                <Link to={params.get('returnTo') as string}>
                    <FormattedMessage
                        id='error.generic.link'
                        defaultMessage='Back to Time'
                    />
                </Link>
            );
        } else if (type === ErrorPageTypes.OAUTH_ACCESS_DENIED || type === ErrorPageTypes.OAUTH_MISSING_CODE) {
            backButton = (
                <Link to='/'>
                    <FormattedMessage
                        id='error.generic.link_login'
                        defaultMessage='Back to Login Page'
                    />
                </Link>
            );
        } else if (type === ErrorPageTypes.OAUTH_INVALID_PARAM || type === ErrorPageTypes.OAUTH_INVALID_REDIRECT_URL) {
            backButton = null;
        } else {
            backButton = (
                <Link to='/'>
                    <FormattedMessage
                        id='error.generic.link'
                        defaultMessage='Back to {siteName}'
                        values={{
                            siteName: this.props.siteName,
                        }}
                    />
                </Link>
            );
        }

        return (
            <div className='container-fluid'>
                <div className='error__container'>
                    <div className='error__icon'>
                        <WarningIcon />
                    </div>
                    <h2 data-testid='errorMessageTitle'>
                        <ErrorTitle
                            type={type}
                            title={title}
                        />
                    </h2>
                    <ErrorMessage
                        type={type}
                        message={message}
                        service={service}
                        isGuest={isGuest}
                    />
                    {backButton}
                </div>
            </div>
        );
    }

    private isSAMLError(message: string) {
        return Object.values(SamlErrorMessages).includes(message.trim());
    }
}
